import { useCallback, useEffect } from 'react';
import { PrimitiveAtom, useAtomValue, useSetAtom } from 'jotai';
import { CameraType, Number3 } from '@fabric-web/3d-viewer';
import queryString from 'query-string';
import { updateUrl } from '@/utils';

export type CameraSettingsDeeplinkState = {
  shouldUpdateUrl: boolean;
  cameraTypeAtom: PrimitiveAtom<CameraType>;
  cameraDirectionAtom: PrimitiveAtom<Number3 | undefined>;
};

export const useCameraSettingsDeeplink = (state: CameraSettingsDeeplinkState) => {
  const { shouldUpdateUrl, cameraTypeAtom, cameraDirectionAtom } = state;

  const cameraType = useAtomValue(cameraTypeAtom);
  const cameraDirection = useAtomValue(cameraDirectionAtom);

  const setCameraType = useSetAtom(cameraTypeAtom);
  const setCameraDirection = useSetAtom(cameraDirectionAtom);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl(
        'cameraType',
        cameraType === CameraType.Orthographic ? 'orthographic' : 'perspective'
      );
    }
  }, [shouldUpdateUrl, cameraType]);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl(
        'cameraDirection',
        cameraDirection ? cameraDirection.map((v) => v.toString()) : undefined
      );
    }
  }, [shouldUpdateUrl, cameraDirection]);

  const applyFromParsed = useCallback(
    (parsed: queryString.ParsedQuery<string>) => {
      if (parsed.cameraType && !Array.isArray(parsed.cameraType)) {
        const type =
          { perspective: CameraType.Perspective, orthographic: CameraType.Orthographic }[
            parsed.cameraType
          ] ?? CameraType.Perspective;
        setCameraType(type);
      }

      if (parsed.cameraDirection && Array.isArray(parsed.cameraDirection)) {
        const direction = parsed.cameraDirection.map((value) =>
          value ? Number.parseFloat(value) : 0
        );
        if (direction.length === 3) {
          setCameraDirection(direction as Number3);
        }
      }
    },
    [setCameraType, setCameraDirection]
  );

  return { applyFromParsed };
};
